import { useMutation, useQueryClient } from '@tanstack/react-query';
import { axios } from '@/src/lib/axios/axios';
import { MESSAGING_API_BASE, messagingQueryKeys } from '../config';
import { getRegisteredMessagingStore } from '../../store/messaging.store';

export type DeleteMessageDTO = {
  channelId: number;
  messageId: number;
};

const deleteMessage = async (
  data: DeleteMessageDTO
): Promise<{ success: boolean }> => {
  const { channelId, messageId } = data;

  return axios.delete(
    `${MESSAGING_API_BASE}/channels/${channelId}/messages/${messageId}`
  );
};

export const useDeleteMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DeleteMessageDTO) => deleteMessage(data),
    onMutate: async (variables) => {
      // Track this mutation to prevent socket events from overwriting it
      const store =
        getRegisteredMessagingStore && getRegisteredMessagingStore();
      if (store) {
        store.getState().trackMutation(variables.messageId, {
          type: 'delete',
          timestamp: Date.now(),
          channelId: variables.channelId,
        });
      }

      // Optimistically mark message as deleted in cache immediately for instant UI feedback
      queryClient.setQueriesData(
        {
          predicate: (query) => {
            const key = query.queryKey as any[];
            return (
              key?.[0] === 'messaging' &&
              key.some((k) => k === 'messages') &&
              key.some((k) => k === variables.channelId)
            );
          },
        },
        (old: any) => {
          if (!old || !Array.isArray(old.pages)) return old;
          const updatedPages = old.pages.map((page: any[]) =>
            page.map((msg: any) => {
              const msgId = msg.id || msg['message-id'];
              if (String(msgId) === String(variables.messageId)) {
                return {
                  ...msg,
                  isDeleted: true,
                  'is-deleted': true,
                  text: msg.text
                    ? {
                        ...msg.text,
                        body: 'This message was deleted',
                      }
                    : { body: 'This message was deleted' },
                  deletedAt: Date.now(),
                  'date-updated': new Date().toISOString(),
                };
              }
              return msg;
            })
          );
          return { ...old, pages: updatedPages };
        }
      );
    },
    onSuccess: (data, variables) => {
      // Clear mutation tracker after a short delay (allows socket event to be ignored)
      setTimeout(() => {
        const store =
          getRegisteredMessagingStore && getRegisteredMessagingStore();
        if (store) {
          store.getState().clearMutation(variables.messageId);
        }
      }, 2000);

      // Invalidate to fetch canonical server state - ensures all users see same data
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey as any[];
          return (
            key?.[0] === 'messaging' &&
            key.some((k) => k === 'messages') &&
            key.some((k) => k === variables.channelId)
          );
        },
      });
    },
    onError: (error, variables) => {
      // Clear mutation tracker on error
      const store =
        getRegisteredMessagingStore && getRegisteredMessagingStore();
      if (store) {
        store.getState().clearMutation(variables.messageId);
      }
    },
  });
};
